import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { appSettings } from 'app/appSettings';
import { TranslationHelperService } from 'app/core/services/translate-helper.service';
import { Florder } from 'app/core/interfaces/florder.interface';
import { FlordersFilter } from 'app/core/interfaces/florders-filter.interface';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { Definitions } from 'app/core/store/definitions/definitions.interface';
import { FlowEdit } from 'app/core/store/flows/flow-edit.interface';
import { AuthorizationMatrix } from 'app/core/store/session/authorization/authorization.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { util } from 'app/core/util/util';
import * as _ from 'typedash';
import { ScrollService } from '../../../core/services/scroll.service';
import { ConfirmDialogService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { takeUntil } from 'rxjs/operators';
import { Destroyable } from '@dlw-int-mwi/eps-common';
import { MatCheckbox } from '@angular/material/checkbox';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LayoutBreakpoints } from '../../../core/util/layout-breakpoints';

@Component({
  selector: 'app-flow-list',
  templateUrl: './flow-list.component.html',
  styleUrls: ['../../florders/florder-list/florder-list.scss', './flow-list.component.scss'],
})
export class FlowListComponent extends Destroyable implements OnInit, OnChanges {
  @Input() flows: Florder[];
  @Input() readonly definitions: Definitions;
  @Input() isDownloading: boolean;
  @Input() isSuperUser: boolean;
  @Input() isLoading: boolean;
  @Input() filter: FlordersFilter;
  @Input() totalItems: number;
  @Input() authorization: AuthorizationMatrix;
  @Input() handshakingOrConfirming: boolean;
  @Input() customersThatCanUpsertFlows: Customer[];
  @Input() customersThatCanAcceptFlows: Customer[];
  @Input() private filterExcecutionTimestamp: Date;

  @Output() cancelFlow: EventEmitter<Florder> = new EventEmitter();
  @Output() acceptFlow: EventEmitter<Florder> = new EventEmitter();
  @Output() acceptFlows: EventEmitter<any> = new EventEmitter();
  @Output() downloadList: EventEmitter<string> = new EventEmitter();
  @Output() openFlow: EventEmitter<Florder> = new EventEmitter();
  @Output() copyFlow: EventEmitter<Florder> = new EventEmitter();
  @Output() pageChange: EventEmitter<number> = new EventEmitter();
  @Output() sortChange: EventEmitter<{ sortField: string; sortAscending: boolean }> = new EventEmitter();
  @Output() editFlow: EventEmitter<{ flow: Florder; isRegistrar: boolean; isHandshaker: boolean }> = new EventEmitter();
  @Output() editDispute: EventEmitter<{ flow: Florder; isRegistrar: boolean; isHandshaker: boolean }> = new EventEmitter();
  @Output() editAcceptFlow: EventEmitter<Florder> = new EventEmitter();
  @Output() downloadDocument: EventEmitter<Florder> = new EventEmitter();
  @Output() itemsPerPageChange = new EventEmitter<number>();

  showAll = true;
  flowListForm: UntypedFormGroup;
  dateFormat: string = appSettings.DATE_FORMAT_ANGULAR;
  selected = 0;
  containsBlacklistedItems = false;
  smallScreen = false;
  canMultiAcceptFlows: boolean = false;
  pageStats: { selected: number; selectable: number; allSelected: boolean; allOtherFlowEditsValid: boolean } = {
    selected: 0,
    selectable: 0,
    allSelected: false,
    allOtherFlowEditsValid: false,
  };

  private collapsedFlows: string[] = [];
  private flowEdits: { [key: string]: FlowEdit } = {};

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translationHelperService: TranslationHelperService,
    private scrollService: ScrollService,
    private confirmDialogService: ConfirmDialogService,
    private breakpointObserver: BreakpointObserver
  ) {
    super();
  }

  ngOnInit(): void {
    const controlsConfig = {
      flows: this.formBuilder.array([]),
    };
    this.flowListForm = this.formBuilder.group(controlsConfig);
    this.updateChecks();
    LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.smallScreenOrLess).pipe(
      takeUntil(this.destroyed$),
    ).subscribe(smallScreen => this.smallScreen = smallScreen);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.flows) {
      this.scrollService.scrollToElement('flow-list');
      this.flows = this.flows.map(flow => {
        return {
          ...flow,
          flowLineItems: flow.flowLineItems.map(item => {
            return {
              ...item,
            };
          }),
        };
      });
      this.updateChecks();
    }
    if (changes.filterExcecutionTimestamp) {
      this.resetFlowEdits();
    }

    if (changes.filter) {
      if (this.filter.customers) {
        this.canMultiAcceptFlows = this.filter.customers.length === 1 && this.isSuperUser;
      }
    }
  }

  resetFlowEdits() {
    this.flowEdits = {};
    this.selected = 0;
    this.pageStats = {
      selectable: 0,
      selected: 0,
      allSelected: false,
      allOtherFlowEditsValid: false,
    };
  }

  /**
   * Update form objects for the current subset of flows shown.  Use already stored data from previous edit actions for current query
   * formEdit => formGroup controls
   */
  updateChecks() {
    if (this.flowListForm) {
      this.pageStats = {
        selectable: 0,
        selected: 0,
        allSelected: false,
        allOtherFlowEditsValid: false,
      };
      this.containsBlacklistedItems = false;
      const flowGroupControls = this.flows.map(flow => {
        let flowEdit: FlowEdit = this.flowEdits['' + flow.etmOrderNumber];
        const canHandshake = this.canAcceptHandshake(flow) || this.canConfirmRegistration(flow);
        if (canHandshake) {
          flow.flowLineItems.forEach(fli => {
            let quantity = fli.definitiveQuantity;
            if (flowEdit) {
              const i = flowEdit.items.find(i => i.itemNumber === fli.itemNumber);
              quantity = i.definitiveQuantity;
            }
            if (flow.status && flow.status['id'] !== appSettings.FLOW_STATUS_WAITING_FOR_SUPPLIER_REGISTRATION) {
              fli.definitiveQuantity = quantity === null ? fli.originalQuantity : quantity;
            }
          });
        }
        if (!flowEdit) {
          flowEdit = {
            flow,
            etmOrderNumber: '' + flow.etmOrderNumber,
            fromId: flow.from.id,
            toId: flow.to.id,
            customerReferenceNumber: flow.customerRefNumber,
            comments: null,
            checked: false,
            collapsed: !this.showAll,
            statusId: flow.status && (flow.status as Definition).id,
            flowType: null,
            valid: true,

            items: flow.flowLineItems.map(fli => {
              return {
                itemNumber: fli.itemNumber,
                packingId: fli.packingId,
                contentId: fli.contentId,
                originalQuantity: fli.originalQuantity,
                definitiveQuantity: fli.definitiveQuantity,
                lineReferenceId: fli.lineReferenceId,
              };
            }),
          } as FlowEdit;
          this.flowEdits['' + flow.etmOrderNumber] = flowEdit;
        }
        flowEdit.flow = flow;
        flow['valid'] = flowEdit.valid;
        if (this.canAcceptHandshake(flow) || this.canConfirmRegistration(flow)) {
          this.pageStats.selectable++;
        } else if (flowEdit.checked && flow.blacklisted) {
          flowEdit.checked = false;
          this.selected--;
        }
        if (flowEdit.checked) {
          flow['isChecked'] = true;
          this.pageStats.selected++;
        }

        this.containsBlacklistedItems = this.containsBlacklistedItems || flow.blacklisted;

        const formGroup = this.formBuilder.group({
          checked: [flowEdit.checked],
          etmOrderNumber: [flowEdit.etmOrderNumber],
          statusId: [flowEdit.statusId],
          canAcceptHandshake: [this.canAcceptHandshake(flow)],
          canConfirmRegistration: [this.canConfirmRegistration(flow)],
          items: this.formBuilder.array(
            flowEdit.items.map(i =>
              this.formBuilder.group({
                itemNumber: [i.itemNumber],
                definitiveQuantity: [i.definitiveQuantity, (flow.editDefinitiveQuantity && this.canAcceptHandshake(flow)) ? [Validators.required, util.getNumericValidator()] : []],
                originalQuantity: [i.originalQuantity, flow.editOriginalQuantity ? [Validators.required] : []],
              })
            )
          ),
        });
        return formGroup;
      });
      const flowArrayControl = this.formBuilder.array(flowGroupControls);
      this.flowListForm.setControl('flows', flowArrayControl);

      // Add conditional validations
      (this.flowListForm.get('flows') as UntypedFormArray).controls.forEach((flowControl: UntypedFormGroup) => {
        const checkboxControl = flowControl.get('checked');
      });

      this.pageStats.allSelected = this.pageStats.selected === this.pageStats.selectable && this.pageStats.selected > 0;

      const etmOrderNumbersOnThisPage = this.flows.map(flow => flow.etmOrderNumber);
      this.pageStats.allOtherFlowEditsValid = !Object.keys(this.flowEdits)
        .filter(key => etmOrderNumbersOnThisPage.indexOf(key) < 0)
        .find(key => {
          const flowEdit = this.flowEdits[key];
          flowEdit.valid = !flowEdit.items.find(item => {
            let isInvalid = flowEdit.flow.editDefinitiveQuantity && !util.isNumeric(item.definitiveQuantity);
            isInvalid = isInvalid || (flowEdit.flow.editOriginalQuantity && !util.isNumeric(item.originalQuantity));
            return isInvalid;
          });
          return flowEdit.checked && !flowEdit.valid;
        });
    }
  }

  private updateToggleSelectAllCheckbox(): void {
    this.pageStats.allSelected = this.pageStats.selected === this.pageStats.selectable && this.pageStats.selected > 0;
  }

  /**
   * When changing sorting / pagination, a new subset of flows will be shown.  Story currently changed / checked flows in memory to be used when accept/cancel flow
   * formGroup controls => flowEdit
   */

  private storeEditFields() {
    (this.flowListForm.get('flows') as UntypedFormArray).controls.forEach(flowControl => {
      const flowEdit = this.flowEdits['' + flowControl.get('etmOrderNumber').value];
      flowEdit.checked = flowControl.get('checked').value;
      (flowControl.get('items') as UntypedFormArray).controls.forEach(itemControl => {
        const itemEdit = flowEdit.items.find(i => i.itemNumber === itemControl.get('itemNumber').value);
        itemEdit.originalQuantity = util.toNumber(itemControl.get('originalQuantity').value);
        itemEdit.definitiveQuantity = util.toNumber(itemControl.get('definitiveQuantity').value);
      });
    });
  }

  onPageChange(pageNr) {
    this.storeEditFields();
    this.pageChange.emit(pageNr);
    this.scrollService.scrollToElement('top');
  }

  /**
   * Check if flow is collapsed or not
   * @param {string} etmOrderNumber of flow to check.  Flow shall always exist in flowEdit object since that object is created by updateChecks (when flow is set)
   * @returns {boolean} collapsed or not
   */
  isCollapsed(etmOrderNumber: string): boolean {
    return this.collapsedFlows.indexOf(etmOrderNumber) >= 0;
  }

  /**
   * set collapsed boolean to other value
   * @param {string} etmOrderNumber of flow to check.  Flow shall always exist in flowEdit object since that object is created by updateChecks (when flow is set)
   * @returns {boolean} collapsed or not
   */
  toggleCollapsed(etmOrderNumber: string): boolean {
    if (this.collapsedFlows.indexOf(etmOrderNumber) >= 0) {
      this.collapsedFlows = _.without(this.collapsedFlows, [etmOrderNumber]);
      return false;
    } else {
      this.collapsedFlows.push(etmOrderNumber);
      return true;
    }
  }

  toggleCollapseAll() {
    if (this.showAll) {
      this.collapsedFlows = this.flows.map(f => f.etmOrderNumber);
    } else {
      this.collapsedFlows = [];
    }
    this.showAll = !this.showAll;
  }

  toggleSelected(checkbox: boolean | MatCheckbox, flow: Florder) {
    let checkboxTrue: boolean;
    if (typeof checkbox === 'boolean') {
      checkboxTrue = checkbox;
    } else {
      checkboxTrue = checkbox.checked;
    }
    this.selected += checkboxTrue ? 1 : -1;
    this.updateToggleSelectAllCheckbox();
    const flowEdit: FlowEdit = this.flowEdits['' + flow.etmOrderNumber];
    flowEdit.checked = checkboxTrue;
    flow['isChecked'] = checkboxTrue;
  }

  toggleSelectAll(selectAll) {
    const isChecked = selectAll.source.checked;
    (this.flowListForm.get('flows') as UntypedFormArray).controls.forEach(flowControl => {
      if (flowControl.get('checked').value !== isChecked && (flowControl.get('canAcceptHandshake').value || flowControl.get('canConfirmRegistration').value)) {
        flowControl.get('checked').setValue(isChecked);
        this.selected += isChecked ? 1 : -1;

        const flowEdit = this.flowEdits['' + flowControl.get('etmOrderNumber').value];
        flowEdit.checked = isChecked;
        const flow = this.flows.find(flow => flow.etmOrderNumber === flowEdit.etmOrderNumber);
        flow['isChecked'] = isChecked;
      }
    });
    // There is an issue with unchecking the checkboxes on flow level.  Therefore, I decided to recreate the flowControls.  That worked...
    // Dirty hack: set flows-array with new objects, so that tablflowListForme is rerendered, and then checkboxes are unchecked !
    this.storeEditFields();
    this.flows = [
      ...this.flows.map(flow => {
        return { ...flow };
      }),
    ];
    this.pageStats.selected = isChecked ? this.pageStats.selectable : 0;
    this.updateToggleSelectAllCheckbox();
  }

  sort(field: string) {
    this.storeEditFields();
    util.sortHelper(this, field);
  }

  sortBnd = this.sort.bind(this);

  preventRouting(event) {
    event?.stopPropagation();
  }

  openMenu(event) {
    event.stopPropagation();
  }

  getContent(id: string): string {
    const brick = id && this.definitions.global.brick.find(b => b.id === id);
    return brick && brick.name;
  }

  buttonsAreSpinning(flow: Florder): boolean {
    return this.handshakingOrConfirming || flow.blacklisted;
  }

  acceptFlowsClicked() {
    if (this.canAcceptConfirmSelected() && !this.isAcceptHandshakeSelectedDisabled()) {
      this.storeEditFields();
      const flowEdits = util.deepCopy(this.flowEdits);
      this.acceptFlows.emit(Object.keys(flowEdits).map(key => flowEdits[key]));
    }
  }

  /**
   * all selected flows are valid and can be submitted for accept/confirm
   */
  canAcceptConfirmSelected() {
    return !(this.containsBlacklistedItems || !this.flowListForm.valid || this.selected === 0 || !this.pageStats.allOtherFlowEditsValid);
  }

  canAcceptHandshake(flow: Florder): boolean {
    if (!flow.canAcceptHandshake) {
      return false;
    }
    if (this.isSuperUser) {
      const customer = this.customersThatCanAcceptFlows.find(c => c.id === flow.customerNumber);
      if (!customer) {
        return false;
      }
    }
    return !!(
      !flow.blacklisted &&
      flow.canAcceptHandshake &&
      this.authorization.FLOW.ACCEPT &&
      (flow.status as Definition).id === appSettings.FLOW_STATUS_WAITING_FOR_HANDSHAKE &&
      !flow.failed
    );
  }

  isAcceptHandshakeDisabled(flow: Florder): boolean {
    return this.handshakingOrConfirming || this.containsBlacklistedItems || !flow['valid'] || flow['isChecked'];
  }

  isAcceptHandshakeSelectedDisabled(): boolean {
    const selectedFlowEdits = Object.values(this.flowEdits).filter(flowEdit => flowEdit.checked);
    const selectedFlowsAreValid = selectedFlowEdits.reduce((result, flowEdit) => result && flowEdit.flow['valid'], true);
    return selectedFlowEdits.length == 0 || !selectedFlowsAreValid;
  }

  acceptFlowClicked(flow: Florder) {
    if (!this.handshakingOrConfirming && !this.containsBlacklistedItems && !flow['isChecked'] && flow['valid']) {
      this.acceptFlow.emit(flow);
    }
  }

  canConfirmRegistration(flow: Florder): boolean {
    if (!flow.canConfirmRegistration) {
      return false;
    }
    if (this.isSuperUser) {
      const customer = this.customersThatCanAcceptFlows.find(c => c.id === flow.customerNumber);
      if (!customer) {
        return false;
      }
    }
    return !!(
      !flow.blacklisted &&
      flow.canConfirmRegistration &&
      (flow.status as Definition).id === appSettings.FLOW_STATUS_WAITING_FOR_SUPPLIER_REGISTRATION &&
      this.authorization.FLOW.ACCEPT &&
      !flow.failed
    );
  }

  isConfirmHandshakeDisabled(flow: Florder): boolean {
    return this.handshakingOrConfirming || this.containsBlacklistedItems || !flow['valid'] || flow['isChecked'];
  }

  confirmFlowClicked(flow: Florder) {
    this.acceptFlowClicked(flow);
  }

  canCancelHandshake(flow: Florder): boolean {
    if (!flow.canCancel) {
      return false;
    }
    if (this.isSuperUser) {
      const customer = this.customersThatCanUpsertFlows.find(c => c.id === flow.customerNumber);
      if (!customer) {
        return false;
      }
    }
    return !!((this.authorization.FLOW.CANCEL || this.isSuperUser) && !flow.blacklisted && flow.canCancel && !flow.failed);
  }

  isCancelHandshakeDisabled(flow: Florder): boolean {
    return this.handshakingOrConfirming || this.containsBlacklistedItems || flow['isChecked'];
  }

  async cancelFlowClicked(flow: Florder) {
    if (!this.handshakingOrConfirming && !this.containsBlacklistedItems && !flow['isChecked']) {
      const answer = await this.confirmDialogService.askConfirmation({ message: 'FLOWS.LIST.LABELS.CANCEL_FLOW_CONFIRM', props: {flowId: flow.etmOrderNumber} });
      if (answer) {
        this.cancelFlow.emit(flow);
      }
    }
  }

  originalQuantityChanged(value: string, flow: any, item: any) {
    item.originalQuantity = value;
    this.validateQuantity(flow);
  }

  definitiveQuantityChanged(value: string, flow: any, item: any) {
    item.definitiveQuantity = value;
    this.validateQuantity(flow);
  }

  private validateQuantity(flow: any) {
    const invalidItem = flow.flowLineItems.find(item => !util.isNumeric(item.definitiveQuantity) || !util.isNumeric(item.originalQuantity));
    Object.values(this.flowEdits).forEach(flowEdit => {
      if (flow.etmOrderNumber === flowEdit.etmOrderNumber) {
        flowEdit.valid = !invalidItem;
        flowEdit.flow['valid'] = !invalidItem;
      }
    })
    flow.valid = !invalidItem;
  }

  canCopyFlow(flow: Florder): boolean {
    // check if there are filters
    return this.isSuperUser
      ? this.checkIfCustomerIsInCreateOrUpdate(flow.customerNumber) && (this.filter.customers ? this.filter.customers.length : false) === 1
      : this.authorization.FLOW.CREATE;
  }

  canEditFlow(flow: Florder): boolean {
    const superUserCanDo = this.checkIfCustomerIsInCreateOrUpdate(flow.customerNumber);
    const nonSuperUserCanDo = this.authorization.FLOW.UPDATE;
    const canEdit = this.canCancelHandshake(flow) && !flow.salesOrderNumber;

    return !!this.isSuperUser ? superUserCanDo && canEdit : nonSuperUserCanDo && canEdit;
  }

  canDisputeFlow(flow: Florder): boolean {
    const superUserCanDo = this.checkIfCustomerIsInCreateOrUpdate(flow.customerNumber);
    const nonSuperUserCanDo = this.authorization.FLOW.CREATE;
    const canExecuteDisputeManagementOnFlow = flow.executeDisputeManagement;
    return canExecuteDisputeManagementOnFlow && (this.isSuperUser ? superUserCanDo : nonSuperUserCanDo);
  }

  checkIfCustomerIsInCreateOrUpdate(customerId: string): boolean {
    if (this.customersThatCanUpsertFlows) {
      return !!this.customersThatCanUpsertFlows.find(c => {
        return c.id === customerId;
      });
    }
    return false;
  }

  showCheckbox(flow: Florder) {
    return this.canAcceptHandshake(flow) || (this.canConfirmRegistration(flow) && !this.isSuperUser) || (this.canMultiAcceptFlows && this.canAcceptHandshake(flow)) || this.canConfirmRegistration(flow)
  }
}
